import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { UserQuery } from '@examdojo/user';
import { Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { PayoutAccountHttpService } from './payout-account-http.service';
import {
  PayoutAccountFormModel,
  PayoutAccountInsertModel,
  PayoutAccountStoreModel,
  PayoutAccountUpdateModel,
} from './payout-account.model';
import { PayoutAccountStore } from './payout-account.store';

@Injectable({
  providedIn: 'root',
})
export class PayoutAccountService {
  constructor(
    private readonly httpService: PayoutAccountHttpService,
    private readonly store: PayoutAccountStore,
    private readonly userQuery: UserQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {
    // No auto-loading - let the component control when to load data
  }

  loadPayoutAccount(): Observable<PayoutAccountStoreModel | null> {
    this.store.setLoading(true);

    return this.httpService.fetchAll().pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_account',
        action: 'fetch',
      }),
      map((accounts) => {
        // RLS should ensure only current user's account is returned
        const account = accounts.length > 0 ? accounts[0] : null;
        this.store.setPayoutAccount(account);
        return account;
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  createPayoutAccount(formData: PayoutAccountFormModel): Observable<PayoutAccountStoreModel> {
    this.store.setLoading(true);

    // Get current user ID synchronously to avoid subscription issues
    const currentUser = this.userQuery.getActive();
    if (!currentUser?.id) {
      this.store.setLoading(false);
      throw new Error('No user logged in');
    }

    const insertData: PayoutAccountInsertModel = {
      user_id: currentUser.id,
      recipient_email: formData.recipientEmail,
      recipient_name: formData.recipientName,
      recipient_currency: formData.recipientCurrency,
    };

    return this.httpService.create(insertData).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_account',
        action: 'create',
      }),
      tap((account) => {
        this.store.setPayoutAccount(account);
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  updatePayoutAccount(userId: string, formData: Partial<PayoutAccountFormModel>): Observable<PayoutAccountStoreModel> {
    this.store.setLoading(true);

    const updateData: PayoutAccountUpdateModel = {};

    if (formData.recipientEmail !== undefined) {
      updateData.recipient_email = formData.recipientEmail;
    }

    if (formData.recipientName !== undefined) {
      updateData.recipient_name = formData.recipientName;
    }

    if (formData.recipientCurrency !== undefined) {
      updateData.recipient_currency = formData.recipientCurrency;
    }

    return this.httpService.update(userId, updateData).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_account',
        action: 'update',
      }),
      tap((account) => {
        this.store.setPayoutAccount(account);
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  deletePayoutAccount(userId: string): Observable<void> {
    this.store.setLoading(true);

    return this.httpService.delete(userId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'validation.entity.payout_account',
        action: 'delete',
      }),
      tap(() => {
        this.store.setPayoutAccount(null);
      }),
      finalize(() => {
        this.store.setLoading(false);
      }),
    );
  }

  reset(): void {
    this.store.resetToInitialState();
  }
}
